import { GameState } from './GameState';
import { EventBus, GameEvents } from '../core/EventBus';
import { GameConfig } from '../core/GameConfig';
import { GameData } from '../data/GameData';
import { AttributeSystem } from './AttributeSystem';
import { ScheduleDef, ScheduleSlot, AttributeKey } from '../data/types';
import { scriptSystem } from './ScriptSystem';
import { endorsementSystem } from './EndorsementSystem';
import { noticeSystem } from './NoticeSystem';
import { assistantSystem } from './AssistantSystem';
import { ORIGIN_ORDINARY } from '../data/tables/Tags';

/**
 * 特训 → 能力 tag（特训重做批 2026-10-30）：能力 tag 的唯一获取途径=对应特训累计 10 次。
 * id 与 Schedules.ts 特训区一一对应；不在表里的特训 id 不计数（防御：旧档残留槽位引用已删特训不炸）。
 */
const SPECIAL_TRAINING_TAGS: Record<string, string> = {
    SCHEDULE_COURSE_SPECIAL_HOST: 'TAG_SKILL_HOST',
    SCHEDULE_COURSE_SPECIAL_SINGDANCE: 'TAG_SKILL_SINGDANCE',
    SCHEDULE_COURSE_SPECIAL_MARTIAL: 'TAG_SKILL_MARTIAL',
};

/** 特训练成播报（小助理官方口吻一句话，零弹窗；key=scheduleId） */
const SPECIAL_TRAINING_DONE_TEXT: Record<string, string> = {
    SCHEDULE_COURSE_SPECIAL_HOST: '主持特训练满了，这行你算是站住了。',
    SCHEDULE_COURSE_SPECIAL_SINGDANCE: '唱跳特训练满了，舞台上的气口和体力都练扎实了。',
    SCHEDULE_COURSE_SPECIAL_MARTIAL: '武术特训练满了，打戏你算是能亲自上了。',
};

/**
 * 行程系统
 * 管理行程安排（ScheduleSlot 统一对象）、执行、升级替换
 *
 * 定稿：每周一格 ScheduleSlot { kind, refId, contractId?, title? }
 *  - kind='course'|'rest'：refId=ScheduleDef.id
 *  - kind='work'：refId=ScheduleDef.id（常规工作）或 contractId 关联的合同（签约通告）
 *  - kind='audition'：refId=ScriptDef.id（试镜是一周行程，纯门槛判定）
 */
export class ScheduleSystem {
    private static instance: ScheduleSystem;

    private constructor() { }

    static getInstance(): ScheduleSystem {
        if (!this.instance) {
            this.instance = new ScheduleSystem();
        }
        return this.instance;
    }

    /**
     * 获取玩家当前可用的所有行程（未解锁的一律不显示，不置灰、不占位）
     * 过滤规则：
     *   - 训练路径：同 path 通过 requirePathCounts（累计次数）+ replaces 链只显示当前档；
     *   - 特殊训练：requirePathCounts（双路径）全满足才出现，解锁后永久保留（无 replaces）；
     *   - 工作：requireRank（咖位）+ requirePathCounts（次数）双门槛，同 trade 高档解锁后替换低档；
     *     五路径两档制：一档咖位 Lv.1 无训练条件，二档咖位 Lv.3 且对齐路径累计 ≥16；试镜为独立行程（无咖位门槛、不入表）；
     *   - 休息：恒显示。
     */
    getAvailableSchedules(): ScheduleDef[] {
        const state = GameState.getInstance();
        const all = GameData.SCHEDULES;
        const counts = state.player.pathCourseCounts;
        const rank = state.player.rank;

        const countOf = (k: AttributeKey) => counts?.[k] ?? 0;
        const meetsPathCounts = (rc?: Partial<Record<AttributeKey, number>>) =>
            !rc || Object.entries(rc).every(([k, v]) => countOf(k as AttributeKey) >= (v ?? 0));

        // 单条行程是否已解锁
        const unlocked = (s: ScheduleDef): boolean => {
            if (s.category === 'rest') return true;
            if (s.requireRank !== undefined && rank < s.requireRank) return false;
            if (!meetsPathCounts(s.requirePathCounts)) return false;
            return true;
        };

        // 是否被同路径/同工种内某个「更高档且已解锁」的行程替换（沿 replaces 链间接查找）
        const isReplaced = (s: ScheduleDef): boolean => {
            let higher = all.find(x => x.replaces === s.id);
            while (higher) {
                if (unlocked(higher)) return true;
                higher = all.find(x => x.replaces === higher!.id);
            }
            return false;
        };

        return all.filter(s => unlocked(s) && !isReplaced(s));
    }

    /** 安排行程到指定周次 */
    setSchedule(weekIndex: number, slot: ScheduleSlot | null): boolean {
        const state = GameState.getInstance();
        if (weekIndex < 0 || weekIndex >= state.player.schedule.length) return false;
        state.player.schedule[weekIndex] = slot;
        return true;
    }

    /** 便捷：安排常规课程/工作/休息 */
    setBasicSchedule(weekIndex: number, scheduleId: string): boolean {
        const schedule = GameData.SCHEDULE_MAP[scheduleId];
        if (!schedule) return false;
        return this.setSchedule(weekIndex, {
            kind: schedule.category as 'course' | 'work' | 'rest',
            refId: scheduleId,
            title: schedule.name,
        });
    }

    /** 便捷：安排试镜（kind='audition'，不绑定剧本——剧本在当周执行时的剧本池里选） */
    setAuditionSchedule(weekIndex: number): boolean {
        return this.setSchedule(weekIndex, {
            kind: 'audition',
            refId: 'AUDITION',
            title: '试镜',
        });
    }

    /** 便捷：安排签约通告（kind='work' + contractId） */
    setContractSchedule(weekIndex: number, contractId: string, scriptId: string): boolean {
        const script = GameData.SCRIPT_MAP[scriptId];
        if (!script) return false;
        return this.setSchedule(weekIndex, {
            kind: 'work',
            refId: scriptId,
            contractId,
            title: `《${script.name}》拍摄`,
        });
    }

    /** 便捷：安排品牌活动周（kind='work' + endorseId；2026-11-02 挚友改玩家自由排期） */
    setEndorseSchedule(weekIndex: number, endorsementId: string, brandName: string): boolean {
        return this.setSchedule(weekIndex, {
            kind: 'work',
            refId: 'ENDORSE',
            endorseId: endorsementId,
            title: `${brandName}品牌活动`,
        });
    }

    /** 清空所有行程安排 */
    clearSchedule(): void {
        const state = GameState.getInstance();
        state.player.schedule = [null, null, null, null];
    }

    /**
     * 执行某一行程（由 GameLoop 调用）
     * @returns 资源不足兜底替换为在家休息时的提示文案；正常执行返回 null
     */
    executeSchedule(slot: ScheduleSlot): string | null {
        if (!slot) return null;
        let note: string | null = null;
        switch (slot.kind) {
            case 'audition':
                note = this.executeAudition(slot);
                break;
            case 'work':
                if (slot.endorseId) {
                    // 品牌活动周：体力校验 + 完成活动、当周结费（2026-11-02）
                    note = this.executeEndorse(slot);
                } else if (slot.contractId) {
                    // 签约通告：体力校验 + 扣费 + 推进合同拍摄周
                    note = this.executeContract(slot);
                } else {
                    note = this.executeBasic(slot);
                }
                break;
            default:
                // course / rest
                note = this.executeBasic(slot);
                break;
        }

        EventBus.emit(GameEvents.DAY_PASSED, slot);
        return note;
    }

    /**
     * 执行试镜行程：试镜周执行时一次性扣 20 体力（与课程/工作同款资源校验，
     * 不足则兜底强制休息）；一周试多个剧本不额外扣。
     * @returns 体力不足兜底替换为在家休息时的提示文案；正常执行返回 null
     */
    private executeAudition(slot: ScheduleSlot): string | null {
        const state = GameState.getInstance();

        // 体力不足兜底（2026-10-01 起决策日不置灰、玩家可先排休息后排试镜；不足的周在此落回休息）
        const rest = GameData.SCHEDULE_MAP['SCHEDULE_REST_1'];
        if (state.getEnergy() < GameConfig.AUDITION_ENERGY_COST) {
            return this.forceRest(slot, rest,
                `体力不足（试镜需 ${GameConfig.AUDITION_ENERGY_COST}），本周改为在家休息。`);
        }

        state.addEnergy(-GameConfig.AUDITION_ENERGY_COST);
        // 试镜周不绑定具体剧本：剧本池嵌入当周内容下方（页面 4a），
        // 玩家在剧本详情弹窗点 [试镜] 时逐本走 scriptSystem.audition(scriptId) 门槛判定
        return null;
    }

    /**
     * 执行签约通告拍摄周（kind='work' + contractId）
     * 与普通工作一致消耗体力（CONTRACT_ENERGY_COST）；体力不足 → 兜底强制休息，不推进合同拍摄周
     * @returns 资源不足兜底替换为在家休息时的提示文案；正常执行返回 null
     */
    private executeContract(slot: ScheduleSlot): string | null {
        const state = GameState.getInstance();

        // 体力不足兜底（2026-10-01 起决策日不置灰、玩家可先排休息后排通告；不足的周在此落回休息）
        const rest = GameData.SCHEDULE_MAP['SCHEDULE_REST_1'];
        if (state.getEnergy() < GameConfig.CONTRACT_ENERGY_COST) {
            return this.forceRest(slot, rest,
                `体力不足（通告拍摄需 ${GameConfig.CONTRACT_ENERGY_COST}），本周改为在家休息。`);
        }

        state.addEnergy(-GameConfig.CONTRACT_ENERGY_COST);
        // 推进合同拍摄周（日历化批：只给这份合同 +1 已拍周；拍满即杀青结算收益）。
        // 其余合同**不动**——它们的日历由 shootStartAbs 定死，不靠玩家排期推进。
        scriptSystem.shootOneWeek(slot.contractId);
        return null;
    }

    /**
     * 执行品牌活动周（kind='work' + endorseId）
     * 与拍戏同体力口径（CONTRACT_ENERGY_COST）；体力不足 → 兜底强制休息，活动不算完成（月末按逾期违约）
     */
    private executeEndorse(slot: ScheduleSlot): string | null {
        const state = GameState.getInstance();
        const rest = GameData.SCHEDULE_MAP['SCHEDULE_REST_1'];
        if (state.getEnergy() < GameConfig.CONTRACT_ENERGY_COST) {
            return this.forceRest(slot, rest,
                `体力不足（品牌活动需 ${GameConfig.CONTRACT_ENERGY_COST}），本周改为在家休息。`);
        }
        state.addEnergy(-GameConfig.CONTRACT_ENERGY_COST);
        endorsementSystem.completeFriendEvent(slot.endorseId!);
        return null;
    }

    /**
     * 执行常规行程（课程/工作/休息）
     * @returns 资源不足兜底替换为在家休息时的提示文案；正常执行返回 null
     */
    private executeBasic(slot: ScheduleSlot): string | null {
        const schedule = GameData.SCHEDULE_MAP[slot.refId];
        if (!schedule) {
            console.warn(`[ScheduleSystem] 未知行程: ${slot.refId}`);
            return null;
        }

        const state = GameState.getInstance();

        // 资源不足则无法执行：体力/金钱不够 → 文案提示 + 强制休息（该周行程自动替换为 SCHEDULE_REST_1 在家休息）
        // 决策日已不置灰（玩家自由排程，「先休息回血再消耗」属正常玩法），此处既接规划失误也接当月被事件扣资源
        // （金钱允许为负 = 欠债：免费行程 moneyCost=0 不受负数影响，照常执行）
        const rest = GameData.SCHEDULE_MAP['SCHEDULE_REST_1'];
        if (state.getEnergy() < schedule.energyCost) {
            return this.forceRest(slot, rest, `体力不足，${schedule.name}无法执行，本周改为在家休息。`);
        }
        if (schedule.moneyCost > 0 && state.getMoney() < schedule.moneyCost) {
            return this.forceRest(slot, rest, `金钱不足，${schedule.name}无法执行，本周改为在家休息。`);
        }

        // 消耗体力
        state.addEnergy(-schedule.energyCost);

        // 消耗金钱
        if (schedule.moneyCost > 0) {
            state.addMoney(-schedule.moneyCost);
        }

        // 产出：属性提升
        if (schedule.attributeGain) {
            AttributeSystem.getInstance().applyChanges(schedule.attributeGain);
        }

        // ===== 训练：路径累计次数 + 升级判定 + 工作解锁检查（仅训练周结算后；特殊训练无 path 不计数） =====
        if (schedule.category === 'course' && schedule.path) {
            this.settleCourseProgress(schedule);
        }

        // ===== 特训结算（特训重做批 2026-10-30）：特训周完成 → 计数 +1，累计达标授能力 tag =====
        if (schedule.category === 'course' && SPECIAL_TRAINING_TAGS[schedule.id]) {
            this.settleSpecialTraining(schedule);
        }

        // 产出：金钱
        if (schedule.moneyGain > 0) {
            state.addMoney(schedule.moneyGain);
        }

        // 产出：体力恢复
        if (schedule.energyRestore > 0) {
            state.addEnergy(schedule.energyRestore);
        }

        // 产出：名气（跑龙套等特殊行程）
        if (schedule.fameGain && schedule.fameGain > 0) {
            state.addFame(schedule.fameGain);
        }
        return null;
    }

    /**
     * 特训结算（特训重做批 2026-10-30）：完成次数 +1（player.specialTrainingCounts，从 0 起算，
     * 老档不迁移旧特训进度——特训次数此前无价值），累计达 GameConfig.SPECIAL_TRAINING_TAG_AT 次
     * 授对应能力 tag + 小助理播报一句（官方口吻零弹窗）。tag 不可解除（无解除入口是设计），
     * 授后播报不重复；之后继续训练属性照加（executeBasic 主流程不因此中断）。
     */
    private settleSpecialTraining(schedule: ScheduleDef): void {
        const state = GameState.getInstance();
        const tagId = SPECIAL_TRAINING_TAGS[schedule.id];
        if (state.hasTag(tagId)) return;   // 已练成：计数与播报封口，属性照加
        const counts = state.player.specialTrainingCounts ?? (state.player.specialTrainingCounts = {});
        const count = (counts[schedule.id] ?? 0) + 1;
        counts[schedule.id] = count;
        if (count >= GameConfig.SPECIAL_TRAINING_TAG_AT) {
            state.addTag(tagId);
            assistantSystem.push(SPECIAL_TRAINING_DONE_TEXT[schedule.id] ?? `「${GameData.TAG_MAP[tagId]?.name ?? tagId}」练成了。`);
        }
    }

    /**
     * 训练结算：路径累计次数 +1，并判定升级（12 次升中级、30 次升高级）+ 检查新解锁工作。
     * 计数口径 = 路径所有档位历史执行总数（升档替换不清零、只增不减、跨档累加）。
     */
    private settleCourseProgress(schedule: ScheduleDef): void {
        const state = GameState.getInstance();
        const path = schedule.path!;
        // 素人出道（2026-10-19）：每月第一次训练额外 +1 路径进度（当月不用作废，次月重新计）
        const baseCount = (state.player.pathCourseCounts[path] ?? 0) + 1;
        const absMonth = state.player.year * 12 + state.player.month;
        let originBonus = 0;
        if (state.hasOrigin(ORIGIN_ORDINARY) && state.player.originTrainingMonth !== absMonth) {
            originBonus = 1;
            state.player.originTrainingMonth = absMonth;
        }
        const count = baseCount + originBonus;
        state.player.pathCourseCounts[path] = count;

        // 升级判定：按**基础次数**卡升档门槛（12/30），素人额外进度可以越过门槛但不许跳过判定：
        // 例：11 次 + 素人 +1 = 13，仍以 baseCount=12 触发中级；下月 base=14 不会重复触发。
        const isLevel1To2 = schedule.level === 1 && baseCount === 12;
        const isLevel2To3 = schedule.level === 2 && baseCount === 30;
        if (isLevel1To2 || isLevel2To3) {
            const nextLevel = isLevel1To2 ? 2 : 3;
            const next = GameData.SCHEDULES.find(s => s.path === path && s.level === nextLevel);
            if (next) {
                // 2026-10-25 台词表导码（docs/夏夏台词-文案填写表.md #8）：旧课程名经 replaces 链反查
                const oldName = GameData.SCHEDULE_MAP[next.replaces ?? '']?.name;
                // 台词表 #8（~豁免昵称）
                noticeSystem.push('courseUpgrade',
                    oldName ? `「${oldName}」升级为「${next.name}」了。` : `训练升级为「${next.name}」了。`);
            }
        }

        // 路径计数变化可能解锁新工作（咖位 + 次数双门槛）
        this.notifyWorkUnlocksIfNew();
    }

    /**
     * 检查并弹提示「新解锁的工作」：咖位 + 路径累计次数双门槛均满足、且此前未提示过的工作。
     * 用 flag 去重（工作解锁是单调的：咖位只升不降、路径计数只增不减），避免重复播报。
     * 触发点：训练结算后（本方法）+ 咖位晋升后（RankSystem 调用）。
     */
    notifyWorkUnlocksIfNew(): void {
        const state = GameState.getInstance();
        const rank = state.player.rank;
        const counts = state.player.pathCourseCounts;

        GameData.SCHEDULES.forEach(s => {
            if (s.category !== 'work') return;
            // 无门槛工作（无咖位门槛、无训练解锁条件）开局即有，不存在「解锁瞬间」，不弹提示
            if (s.requireRank === undefined && !s.requirePathCounts) return;
            if (s.requireRank !== undefined && rank < s.requireRank) return;
            if (s.requirePathCounts) {
                for (const [k, v] of Object.entries(s.requirePathCounts)) {
                    if ((counts?.[k as AttributeKey] ?? 0) < (v ?? 0)) return;
                }
            }
            const flagKey = 'notifiedWorkUnlock:' + s.id;
            if (!state.hasFlag(flagKey)) {
                state.setFlag(flagKey);
                noticeSystem.push('workUnlock', `「${s.name}」解锁了。`);
            }
        });
    }

    /**
     * 资源不足兜底：将当前周行程替换为 SCHEDULE_REST_1 在家休息并执行
     * @returns 兜底提示文案
     */
    private forceRest(slot: ScheduleSlot, rest: ScheduleDef, logText: string): string {
        const state = GameState.getInstance();
        const idx = state.player.schedule.indexOf(slot);
        if (idx >= 0) {
            state.player.schedule[idx] = {
                kind: 'rest',
                refId: rest.id,
                title: rest.name,
            };
        }
        // 在家休息无消耗，必定通过资源检查，直接执行（不会递归）
        this.executeBasic({
            kind: 'rest',
            refId: rest.id,
            title: rest.name,
        });
        return logText;
    }

    /** 获取行程的最终版（沿替换链找到最高级） */
    getUltimateSchedule(scheduleId: string): ScheduleDef {
        const map = GameData.SCHEDULE_MAP;
        let current = map[scheduleId];
        if (!current) return null!;

        // 看看有没有更高级的版本替换了它
        const upgraded = Object.values(map).find(s => s.replaces === current?.id);
        if (upgraded) {
            return this.getUltimateSchedule(upgraded.id);
        }
        return current;
    }

    /** 检查当前行程安排是否完整（4周都已安排） */
    isScheduleComplete(): boolean {
        const state = GameState.getInstance();
        return state.player.schedule.every(s => s !== null);
    }
}

/** 导出单例 */
export const scheduleSystem = ScheduleSystem.getInstance();